/// Player-issued command verbs that mutate aircraft target* fields.
/// Mirrors a slim subset of FlatoutATC/Models/GameViewModel.swift.
///
/// `setHeading` / `setAltitude` are best-effort — they don't validate
/// against current aircraft status. `commandTakeoff` is delegated to
/// the autopilot (which updates more state than just a target).

import type { Aircraft } from './Aircraft'
import type { Airport, LevelData, Runway } from '../types/LevelData'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import { performanceFor } from './AircraftDatabase'
import {
  commandGoAround,
  commandTakeoff as autopilotTakeoff,
  issueApproachClearance,
  type ApproachClearanceResult,
} from './Autopilot'

/// Result of a target-clamping command. `commanded` is what the
/// aircraft actually accepted (post-clamp); `clamped` is true when
/// the player's input was outside the type's envelope; `limit`
/// names which gate clipped it. The chrome uses this to surface an
/// "Unable — limited to <X>" readback and rewrite its input field
/// to the accepted value.
export interface ClampedCommandResult {
  commanded: number
  clamped: boolean
  limit: 'min' | 'max' | null
}

export function setHeading(
  aircraft: Aircraft,
  headingDeg: number,
  direction: 'shortest' | 'left' | 'right' = 'shortest',
): number {
  let v = headingDeg % 360
  if (v < 0) v += 360
  aircraft.targetHeadingDeg = v
  aircraft.commandedTurnDirection = direction
  // Manual vector cancels any active localizer-intercept autopilot —
  // otherwise tickApproachIntercept would overwrite the new heading
  // on the next frame.
  aircraft.approachIntercept = null
  // Same reasoning for an active hold: vectoring out of a racetrack
  // should drop the hold rather than fight it.
  aircraft.holdingState = null
  // A manual heading overrides any auto-engaged survey hold timer
  // too — the player is taking control of this aircraft now.
  aircraft.surveyHoldRemainingSec = null
  // Vectoring also cancels an active SID/STAR. The directTo slot is
  // cleared so tickFlightPlan won't fight the manual heading on the
  // next tick.
  aircraft.flightPlan = null
  aircraft.directToPoint = null
  aircraft.directToName = null
  return v
}

export function setAltitude(aircraft: Aircraft, altitudeFt: number): ClampedCommandResult {
  const perf = performanceFor(aircraft.type)
  return clampAndAssign(altitudeFt, perf.minAltitudeFt, perf.maxAltitudeFt, (v) => {
    aircraft.targetAltitudeFt = v
  })
}

/// Player-issued target speed, clamped to the aircraft type's
/// published min/max. Aircraft.advanceSpeed integrates toward the
/// target at the type's normalLandingDecelRate.
export function setSpeed(aircraft: Aircraft, speedKts: number): ClampedCommandResult {
  const perf = performanceFor(aircraft.type)
  return clampAndAssign(speedKts, perf.minSpeedKts, perf.maxSpeedKts, (v) => {
    aircraft.targetSpeedKts = v
  })
}

function clampAndAssign(
  raw: number,
  min: number,
  max: number,
  assign: (v: number) => void,
): ClampedCommandResult {
  const rounded = Math.round(raw)
  let limit: 'min' | 'max' | null = null
  let commanded = rounded
  if (rounded < min) {
    commanded = min
    limit = 'min'
  } else if (rounded > max) {
    commanded = max
    limit = 'max'
  }
  assign(commanded)
  return { commanded, clamped: limit !== null, limit }
}

export function commandTakeoff(
  aircraft: Aircraft,
  level: LevelData,
  projector: CoordinateProjector,
): { ok: boolean; reason?: string } {
  return autopilotTakeoff(aircraft, level, projector)
}

/// Issue a "Cleared for approach" — engage the localizer-intercept
/// autopilot toward the given runway end. Altitude stays under
/// player control until they issue Cleared to land.
export function commandClearedForApproach(args: {
  aircraft: Aircraft
  airport: Airport
  runway: Runway
  approachHeadingDeg: number
  projector: CoordinateProjector
  pxPerNM: number
}): ApproachClearanceResult {
  return issueApproachClearance(args)
}

/// Set or clear the player's direct-to target for the given aircraft.
/// `waypointName === null` cancels any active direct-to. Returns ok=false
/// if the aircraft is on the ground or the named waypoint isn't on the
/// level.
export function commandDirectTo(
  aircraft: Aircraft,
  waypointName: string | null,
  level: LevelData,
  projector: CoordinateProjector,
): { ok: boolean; reason?: 'on_ground' | 'unknown_waypoint' } {
  if (
    aircraft.status === 'awaitingTakeoff' ||
    aircraft.status === 'landed' ||
    aircraft.status === 'completed'
  ) {
    return { ok: false, reason: 'on_ground' }
  }
  if (waypointName == null) {
    aircraft.directToPoint = null
    aircraft.directToName = null
    // Cancel direct also drops any active localizer-intercept — the
    // player is taking over navigation, not just dropping the fix.
    aircraft.approachIntercept = null
    // …and any active hold. Cancel-direct from a hold is a player
    // request to abandon the racetrack and resume own-nav.
    aircraft.holdingState = null
    aircraft.surveyHoldRemainingSec = null
    return { ok: true }
  }
  const wp = level.waypoints.find((w) => w.name === waypointName)
  // Helipads aren't in `level.waypoints` — they ride along on each
  // airport. Fall back to a per-airport pad lookup so helicopters can
  // be vectored to a pad by id (e.g. "HMC") just like a fix.
  let coord = wp?.coordinate
  if (!coord) {
    for (const ap of level.airports) {
      const pad = ap.helipads?.find((p) => p.id === waypointName)
      if (pad) {
        coord = pad.coordinate
        break
      }
    }
  }
  if (!coord) return { ok: false, reason: 'unknown_waypoint' }
  aircraft.directToPoint = projector.project(coord, true)
  aircraft.directToName = waypointName
  // Vectoring to a fix cancels any active Cleared-for-approach
  // intercept — otherwise tickApproachIntercept would steal the
  // heading back to the runway every frame and the aircraft would
  // never turn toward the picked waypoint.
  aircraft.approachIntercept = null
  // Same for a hold: a fresh direct-to ends the racetrack.
  aircraft.holdingState = null
  aircraft.surveyHoldRemainingSec = null
  return { ok: true }
}

/// Player-issued "Hold at <fix>". Engages a four-phase right-hand
/// racetrack autopilot driven by `tickHold`. If the aircraft is on a
/// landing approach, this also triggers a go-around — matches iOS
/// behavior at GameViewModel.swift:2120-2125 (issuing Hold during
/// landing aborts the landing).
export function commandHold(args: {
  aircraft: Aircraft
  waypointName: string
  level: LevelData
  projector: CoordinateProjector
  airports: Airport[]
}): { ok: boolean; reason?: 'on_ground' | 'unknown_waypoint'; goAround?: boolean } {
  const { aircraft, waypointName, level, projector, airports } = args
  if (
    aircraft.status === 'awaitingTakeoff' ||
    aircraft.status === 'landed' ||
    aircraft.status === 'completed' ||
    aircraft.status === 'takingOff'
  ) {
    return { ok: false, reason: 'on_ground' }
  }
  const wp = level.waypoints.find((w) => w.name === waypointName)
  if (!wp) return { ok: false, reason: 'unknown_waypoint' }

  // Issuing Hold while on landing approach acts as a Go-around.
  let goAround = false
  if (aircraft.status === 'landing') {
    commandGoAround(aircraft, airports)
    goAround = true
  }
  // Cancel anything else that drives heading.
  aircraft.approachIntercept = null

  const fixPoint = projector.project(wp.coordinate, true)
  // Bearing from current position to the fix is the *inbound* leg of
  // the racetrack — the heading the aircraft is on as it crosses the
  // fix the first time.
  const dx = fixPoint.x - aircraft.position.x
  const dy = fixPoint.y - aircraft.position.y
  const screenAngle = Math.atan2(dy, dx)
  let inboundHeadingDeg = (screenAngle * 180) / Math.PI + 90
  if (inboundHeadingDeg < 0) inboundHeadingDeg += 360
  if (inboundHeadingDeg >= 360) inboundHeadingDeg -= 360

  aircraft.holdingState = {
    fixName: wp.name,
    fixPoint,
    inboundHeadingDeg,
    phase: 'flyingToWaypoint',
    outboundTimerSec: 0,
    hasScored: false,
  }
  // The hold autopilot owns navigation now — the aircraft's
  // direct-to slot is unused (tickHold reads holdingState.fixPoint).
  aircraft.directToPoint = null
  aircraft.directToName = null
  return { ok: true, goAround }
}

/// Player-issued Cancel hold. No-op if the aircraft isn't currently
/// holding. Returns ok=false in that case so the chrome can decide
/// whether to surface a readback.
export function commandCancelHold(aircraft: Aircraft): { ok: boolean } {
  if (!aircraft.holdingState) return { ok: false }
  aircraft.holdingState = null
  // Cancelling an auto-engaged survey hold dismisses the timer; the
  // aircraft will need a fresh direct-to before tickSurvey can re-
  // engage. Matches iOS GameViewModel.swift:2272 (player cancelHold
  // path sets surveyHoldRemainingSec = nil).
  aircraft.surveyHoldRemainingSec = null
  return { ok: true }
}
